import { Button, IconButton, Typography } from "@suraasa/placebo-ui"
import clsx from "clsx"
import { format } from "date-fns"
import SuraasaPlusLogo from "features/SuraasaPlus/components/SuraasaPlusLogo"
import { useSuraasaPlusContext } from "features/SuraasaPlus/context"
import { Xmark } from "iconoir-react"

import lightOverlay from "../assets/light_layer.webp"
import darkOverlay from "../assets/top_dark_layer.webp"
import { DialogThemeMode } from "../utils"
import AdaptiveDialogAndSheet from "./AdaptiveDialogAndSheets"

type Props = {
  open: boolean
  handleClose: () => void
  mode?: DialogThemeMode
  handleClick?: () => void
}

const cards = [
  {
    heading: "AI Tools",
    text: (
      <>
        Get <span className="text-strongSmallBody">unlimited generations</span>{" "}
        for all AI Teaching Tools.
      </>
    ),
  },
  {
    heading: "Jobs",
    text: (
      <>
        Apply to{" "}
        <span className="text-strongSmallBody">
          unlimited premium teaching jobs
        </span>{" "}
        at top schools around the world.
      </>
    ),
  },
  {
    heading: "Masterclasses",
    text: (
      <>
        Get <span className="text-strongSmallBody">unlimited access</span> to
        150+ expert-led, certified masterclasses.
      </>
    ),
  },
  {
    heading: "Webinars",
    text: (
      <>
        Attend <span className="text-strongSmallBody">unlimited webinars</span>{" "}
        and <span className="text-strongSmallBody">earn certificates</span> for
        every session.
      </>
    ),
  },
]

const modeStyles = {
  [DialogThemeMode.dark]: {
    body: "bg-[#0E1525]",
    header:
      "bg-[linear-gradient(180deg,#4C1278_0%,#082743_56.82%,#0E1525_100%)]",
    footer: "bg-[#0E1525]",
    cardBg: "bg-onSurface-800",
    text: {
      primary1: "text-white",
      primary2: "text-white",
      secondary: "text-onSurface-400",
      cardHeading: "text-onSurface-100",
      cardText: "text-onSurface-400",
    },
    buttons: {
      x: "text-surface hover:bg-onSurface-200/20  active:bg-onSurface-300/40 active:text-onSurface-900",
      text: "text-onSurface-400",
      primary: "bg-surface text-onSurface-900",
    },
  },
  [DialogThemeMode.light]: {
    body: "bg-white",
    header: "bg-white",
    footer: "bg-gradient-to-b from-white/0 via-white to-white",
    cardBg: "bg-onSurface-100",
    text: {
      primary1: "text-onSurface-600",
      primary2: "text-black",
      secondary: "text-onSurface-500",
      cardHeading: "text-onSurface-800",
      cardText: "text-onSurface-500",
    },
    buttons: {
      x: "text-onSurface-700 hover:bg-gray-100 active:bg-gray-200",
      text: "text-onSurface-600",
      primary: "bg-onSurface-900 text-white",
    },
  },
}

const WelcomeToSuraasaPlus = ({
  open,
  handleClose,
  mode = DialogThemeMode.dark,
}: Props) => {
  const styles = modeStyles[mode]
  const overlayImg = mode === DialogThemeMode.dark ? darkOverlay : lightOverlay
  const { subscriptionDetails } = useSuraasaPlusContext()

  const freeTrialEndsOn = subscriptionDetails?.subscription?.validThrough

  return (
    <AdaptiveDialogAndSheet
      bodyClassName={clsx(styles.body, "p-0")}
      sheetHeight={95}
      open={open}
      handleClose={handleClose}
      closeWhenInteractOutside
      contentClassName="overflow-hidden rounded-xl"
      body={
        <>
          <div className={clsx(styles.header, "relative py-2 pl-3 pr-2")}>
            <img
              src={overlayImg}
              alt=""
              className="pointer-events-none absolute -top-px left-0"
            />
            <div className="flex justify-end">
              <IconButton
                className={clsx("rounded-lg", styles.buttons.x)}
                onClick={handleClose}
              >
                <Xmark className="!size-2" strokeWidth={2.5} />
              </IconButton>
            </div>
            <div>
              <Typography
                className={clsx(
                  "w-fit text-largeTitleMobile",
                  styles.text.primary1
                )}
              >
                Welcome to
              </Typography>
              <SuraasaPlusLogo
                height={36}
                width={224}
                color={mode === DialogThemeMode.dark ? "light" : "dark"}
              />
              <Typography
                className={clsx("mt-2 max-w-[290px]", styles.text.secondary)}
                variant="largeBody"
              >
                Your{" "}
                <span
                  className={clsx("text-onSurface-300", {
                    "text-onSurface-800": mode === DialogThemeMode.light,
                  })}
                >
                  7-day
                </span>{" "}
                free trial starts now. Here&apos;s what you&apos;ve unlocked
              </Typography>
            </div>
          </div>
          <div
            className={clsx(
              styles.body,
              "grid grid-cols-1 gap-1 px-2.5 py-2 sm:grid-cols-2 sm:px-3 sm:pt-1"
            )}
          >
            {cards.map(card => {
              return (
                <div
                  className={clsx(
                    styles.cardBg,
                    "flex flex-col gap-1 rounded-xl p-2"
                  )}
                  key={card.heading}
                >
                  <Typography
                    variant="strongSmallBody"
                    className={clsx("", styles.text.cardHeading)}
                  >
                    {card.heading}
                  </Typography>
                  <Typography
                    variant="smallBody"
                    className={clsx(styles.text.cardText)}
                  >
                    {card.text}
                  </Typography>
                </div>
              )
            })}
          </div>
          <div
            className={clsx(
              "flex w-full flex-col items-start justify-between gap-5 border-0 px-3 pb-3 pt-0 sm:flex-row sm:items-center sm:gap-2 sm:pt-1",
              styles.footer
            )}
          >
            {freeTrialEndsOn && (
              <Typography
                variant="smallBody"
                className={clsx("shrink-0", styles.buttons.text)}
              >
                Free Trial ends on{" "}
                <span className="text-strongSmallBody">
                  {format(new Date(freeTrialEndsOn), "dd MMM")}
                </span>
              </Typography>
            )}
            <Button
              onClick={() => handleClose()}
              variant="filled"
              className={clsx(
                "w-full shrink-0 rounded-[10px] px-2 py-1.5 font-semibold sm:w-fit",
                styles.buttons.primary
              )}
            >
              <Typography variant="strong">Got it</Typography>
            </Button>
          </div>
        </>
      }
    />
  )
}

export default WelcomeToSuraasaPlus
